import React, { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronsUpDown, Search, Star, Plus, Building2, Pin } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Fallback-Icon, falls activeCompany kein Name hat
const Building2Icon = () => <Building2 className="h-5 w-5" />;

const RECENT_KEY = "recentCompanyIds";
const PINNED_KEY = "pinnedCompanyIds";  // Iter108
const LAST_PAGE_KEY_PREFIX = "lastPagePerCompany:";  // Iter108
const MAX_RECENT = 5;

function loadPinned() {
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function togglePin(id) {
  try {
    const pinned = loadPinned();
    const next = pinned.includes(id) ? pinned.filter((x) => x !== id) : [...pinned, id];
    localStorage.setItem(PINNED_KEY, JSON.stringify(next));
    return next;
  } catch {
    return loadPinned();
  }
}

export function rememberLastPage(companyId, path) {
  if (!companyId || !path) return;
  try {
    localStorage.setItem(LAST_PAGE_KEY_PREFIX + companyId, path);
  } catch { /* noop */ }
}

export function getLastPage(companyId) {
  if (!companyId) return null;
  try {
    return localStorage.getItem(LAST_PAGE_KEY_PREFIX + companyId);
  } catch {
    return null;
  }
}

// 8 deterministische Avatar-Farben → Hash company-id auf eine der Farben
const AVATAR_COLORS = [
  "bg-rose-500", "bg-amber-500", "bg-emerald-500", "bg-sky-500",
  "bg-violet-500", "bg-pink-500", "bg-cyan-500", "bg-orange-500",
];

function colorForId(id) {
  if (!id) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function initialsOf(name) {
  if (!name) return "??";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function pushRecent(id) {
  if (!id) return;
  try {
    const recent = loadRecent().filter((x) => x !== id);
    recent.unshift(id);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // noop
  }
}

export function CompanyAvatar({ company, size = "md" }) {
  const cls = size === "sm" ? "h-6 w-6 text-[10px]" : "h-7 w-7 text-[11px]";
  return (
    <span
      className={`${colorForId(company?._id)} ${cls} rounded-md inline-flex items-center justify-center font-semibold text-white flex-shrink-0`}
      data-testid={`company-avatar-${company?._id || "unknown"}`}
    >
      {initialsOf(company?.name)}
    </span>
  );
}

export default function CompanySwitcher({ companies, activeCompany, onSelect, variant = "topbar" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [recent, setRecent] = useState(loadRecent());
  const [pinned, setPinned] = useState(loadPinned());
  const navigate = useNavigate();

  const handleTogglePin = (e, id) => {
    e.stopPropagation();
    setPinned(togglePin(id));
  };

  // Track active company in recent
  useEffect(() => {
    if (activeCompany?._id) {
      pushRecent(activeCompany._id);
      setRecent(loadRecent());
    }
  }, [activeCompany?._id]);

  // Keyboard shortcut Cmd/Ctrl+J
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return companies;
    return companies.filter((c) =>
      (c.name || "").toLowerCase().includes(term)
      || (c.currency || "").toLowerCase().includes(term)
    );
  }, [search, companies]);

  // recents zuerst, dann der Rest, kein Duplikat
  const sorted = useMemo(() => {
    if (search.trim()) return filtered;
    const recentSet = new Set(recent);
    const recentCompanies = recent
      .map((id) => companies.find((c) => c._id === id))
      .filter(Boolean);
    const rest = companies.filter((c) => !recentSet.has(c._id));
    return [...recentCompanies, ...rest];
  }, [filtered, recent, companies, search]);

  const handlePick = (c) => {
    onSelect(c);
    setOpen(false);
    setSearch("");
    // Iter108: Last-Page-Restore – navigiere zur zuletzt besuchten Seite des neuen Mandanten
    const lastPath = getLastPage(c._id);
    if (lastPath && lastPath !== window.location.pathname) {
      // kurz warten bis setCompany propagiert ist, dann navigieren
      setTimeout(() => navigate(lastPath), 50);
    }
  };

  if (!companies?.length) return null;

  // Geteilter Popover-Content (Such-Liste mit Mandanten-Switch) — wird sowohl
  // vom Topbar- als auch vom Sidebar-Trigger genutzt.
  const popoverContent = (
    <PopoverContent className="w-80 p-0" align="start" data-testid="company-switcher-popover">
      <div className="p-2 border-b border-slate-100 flex items-center gap-2">
        <Search className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suchen … (Ctrl+J)"
          className="h-7 border-0 shadow-none focus-visible:ring-0 px-0 text-sm"
          autoFocus
          data-testid="company-switcher-search"
        />
        <kbd className="hidden sm:inline text-[10px] font-mono bg-slate-100 text-slate-500 rounded px-1 py-0.5">
          {navigator.platform.includes("Mac") ? "⌘J" : "Ctrl+J"}
        </kbd>
      </div>
      <div className="max-h-80 overflow-y-auto py-1" data-testid="company-switcher-list">
        {sorted.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400">Kein Treffer</div>
        ) : (
          sorted.map((c, idx) => {
            const isActive = activeCompany?._id === c._id;
            const isPinned = pinned.includes(c._id);
            const isRecent = !search.trim() && !isPinned && idx < pinned.length + Math.min(recent.length, MAX_RECENT) && recent.includes(c._id);
            return (
              <button
                key={c._id}
                type="button"
                onClick={() => handlePick(c)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-slate-50 transition-colors text-left ${
                  isActive ? "bg-slate-100" : ""
                }`}
                data-testid={`company-switcher-item-${c._id}`}
              >
                <CompanyAvatar company={c} size="sm" />
                <span className="flex-1 min-w-0">
                  <span className="block truncate font-medium text-slate-900">{c.name}</span>
                  <span className="block truncate text-[10px] text-slate-400">
                    {c.currency || "CHF"}
                    {c.role ? ` · ${c.role}` : ""}
                  </span>
                </span>
                {isPinned && !isActive && (
                  <Pin className="h-3 w-3 text-violet-500 fill-violet-500 flex-shrink-0" data-testid={`company-switcher-pinned-${c._id}`} />
                )}
                {isRecent && !isActive && (
                  <Star className="h-3 w-3 text-amber-400 flex-shrink-0" data-testid={`company-switcher-recent-${c._id}`} />
                )}
                {isActive && <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
                <span
                  onClick={(e) => handleTogglePin(e, c._id)}
                  className="opacity-0 group-hover:opacity-100 hover:opacity-100 ml-1 text-slate-300 hover:text-violet-600 cursor-pointer"
                  data-testid={`company-switcher-pin-toggle-${c._id}`}
                  title={isPinned ? "Anpinnen entfernen" : "Anpinnen"}
                >
                  <Pin className={`h-3 w-3 ${isPinned ? "fill-violet-500 text-violet-500" : ""}`} />
                </span>
              </button>
            );
          })
        )}
      </div>
      <div className="p-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
        <span>{companies.length} Mandanten</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-[11px] px-2"
          onClick={() => {
            setOpen(false);
            navigate("/new-company");
          }}
          data-testid="company-switcher-new-btn"
        >
          <Plus className="h-3 w-3 mr-1" />
          Neu anlegen
        </Button>
      </div>
    </PopoverContent>
  );

  // ---------- Sidebar variant: Brand block in the sidebar (always clickable, even with 1 company) ----------
  const sidebarTrigger = (
    <button
      type="button"
      className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors text-left group"
      data-testid="sidebar-company-switcher-trigger"
    >
      <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shadow-sm flex-shrink-0">
        {activeCompany?.name ? (
          <span className="text-sm font-bold">{initialsOf(activeCompany.name)}</span>
        ) : (
          <Building2Icon />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
          Buchhaltung
        </div>
        <div
          className="text-sm font-semibold text-slate-900 truncate"
          title={activeCompany?.name}
        >
          {activeCompany?.name || "Mandant wählen"}
        </div>
      </div>
      {companies.length > 1 && (
        <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 group-hover:text-slate-600" />
      )}
    </button>
  );

  if (variant === "sidebar") {
    // Single-company → still show brand block, but no popover
    if (companies.length === 1) {
      return sidebarTrigger;
    }
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{sidebarTrigger}</PopoverTrigger>
        {popoverContent}
      </Popover>
    );
  }

  // Single-company → simple read-only pill
  if (companies.length === 1) {
    return (
      <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1" data-testid="topbar-company-single">
        <CompanyAvatar company={activeCompany} size="sm" />
        <span className="text-sm font-medium text-slate-900 truncate max-w-[180px]">
          {activeCompany?.name}
        </span>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 hover:bg-slate-50 transition-colors min-w-0 max-w-[260px]"
          data-testid="topbar-company-switcher-trigger"
        >
          <CompanyAvatar company={activeCompany} size="sm" />
          <span className="text-sm font-medium text-slate-900 truncate flex-1 text-left">
            {activeCompany?.name || "Mandant wählen"}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      {popoverContent}
    </Popover>
  );
}
